import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material';

/**
 * Modern textured background styles
 * Various patterns and gradients for hero sections and page backgrounds
 */

export const getTexturedHeroBackground = (theme: Theme): SxProps => ({
  position: 'relative',
  overflow: 'hidden',
  // Multi-layer textured background with enhanced texture
  background: `
    linear-gradient(135deg, ${theme.palette.secondary.main}15 0%, ${theme.palette.secondary.light}08 50%, ${theme.palette.primary.main}10 100%),
    radial-gradient(circle at 20% 50%, ${theme.palette.secondary.main}12 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, ${theme.palette.primary.main}10 0%, transparent 50%),
    radial-gradient(circle at 50% 30%, ${theme.palette.secondary.light}08 0%, transparent 40%),
    repeating-linear-gradient(
      45deg,
      transparent,
      transparent 2px,
      rgba(255, 255, 255, 0.03) 2px,
      rgba(255, 255, 255, 0.03) 4px
    ),
    repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 3px,
      rgba(0, 0, 0, 0.01) 3px,
      rgba(0, 0, 0, 0.01) 6px
    ),
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 1px,
      rgba(255, 255, 255, 0.02) 1px,
      rgba(255, 255, 255, 0.02) 2px
    ),
    linear-gradient(to bottom, ${theme.palette.mode === 'dark' ? '#010409 0%, #020617 100%' : '#f8f9fa 0%, #ffffff 100%'})
  `,
  backgroundSize: '100% 100%, 100% 100%, 100% 100%, 100% 100%, 20px 20px, 15px 15px, 100% 4px, 100% 100%',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 30% 20%, rgba(0, 187, 255, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 70% 60%, rgba(0, 187, 255, 0.10) 0%, transparent 50%),
      radial-gradient(circle at 10% 80%, rgba(0, 187, 255, 0.08) 0%, transparent 40%),
      radial-gradient(circle at 90% 20%, rgba(0, 187, 255, 0.08) 0%, transparent 40%)
    `,
    pointerEvents: 'none',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      repeating-conic-gradient(
        from 0deg at 50% 50%,
        transparent 0deg,
        rgba(255, 255, 255, 0.01) 1deg,
        transparent 2deg,
        rgba(0, 0, 0, 0.005) 3deg
      )
    `,
    backgroundSize: '200px 200px',
    opacity: 0.5,
    pointerEvents: 'none',
  },
});

export const getSubtlePatternBackground = (theme: Theme): SxProps => ({
  background: `
    repeating-linear-gradient(
      0deg,
      transparent,
      transparent 2px,
      rgba(0, 0, 0, 0.02) 2px,
      rgba(0, 0, 0, 0.02) 4px
    ),
    linear-gradient(to bottom, ${theme.palette.mode === 'dark' ? '#1E293B 0%, #0F172A 100%' : '#ffffff 0%, #f8f9fa 100%'})
  `,
  backgroundSize: '100% 4px, 100% 100%',
});

export const getGradientBackground = (theme: Theme, direction: 'vertical' | 'diagonal' = 'vertical'): SxProps => {
  const gradient = direction === 'diagonal'
    ? `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}05 100%)`
    : `linear-gradient(to bottom, ${theme.palette.primary.main}05 0%, transparent 100%)`;

  return {
    background: gradient,
  };
};

